// src/pages/SellerIntelligence/MetricsPlayground/MetricsPlayground.js
// Development harness for the Seller Intelligence metric library.
//
// Every function in the library is pure, so this page only has to move JSON:
// pick a function, optionally edit the sample inputs or config, and post it. The
// backend routes are dev-only, so the page degrades to an explanation when the
// harness is switched off rather than showing a network error.
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FiArrowLeft, FiPlay, FiRefreshCw, FiCode } from 'react-icons/fi';

import styles from '../SellerIntelligence.module.css';
import local from './MetricsPlayground.module.css';
import AuthContext from '../../../context/AuthContext';
import ResultView from './components/ResultView';
import { getFunctions, getSample, runFunction, runFunctionLive } from './services/metricsApi';

const BUCKETS = ['DAY', 'WEEK', 'MONTH'];

// datetime-local inputs will not accept seconds or a timezone suffix.
const toLocalInput = (iso) => (iso ? iso.slice(0, 16) : '');

const MetricsPlayground = () => {
  const navigate = useNavigate();
  const authCtx = useContext(AuthContext);
  const token = authCtx?.token;

  const [selected, setSelected] = useState(null);
  const [inputsText, setInputsText] = useState('');
  const [configText, setConfigText] = useState('');
  const [inputCounts, setInputCounts] = useState('');
  const [options, setOptions] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [running, setRunning] = useState(false);
  const [showRaw, setShowRaw] = useState(false);
  const [source, setSource] = useState('sample');
  const [liveNotes, setLiveNotes] = useState(null);

  const catalogue = useQuery({
    queryKey: ['metrics-playground-functions'],
    queryFn: () => getFunctions(token),
    enabled: !!token,
    staleTime: Infinity,
    retry: false,
  });

  // Memoised so the fallback array is not a fresh reference on every render,
  // which would invalidate the two memos below each time.
  const specs = useMemo(() => catalogue.data?.functions ?? [], [catalogue.data]);
  const currentSpec = useMemo(
    () => specs.find((spec) => spec.name === selected) ?? null,
    [specs, selected],
  );

  // Group into the three families the doc assigns, preserving registry order.
  const families = useMemo(() => {
    const grouped = new Map();
    specs.forEach((spec) => {
      if (!grouped.has(spec.family)) grouped.set(spec.family, []);
      grouped.get(spec.family).push(spec);
    });
    return [...grouped.entries()];
  }, [specs]);

  useEffect(() => {
    if (!catalogue.data || selected) return;
    setOptions({
      start: catalogue.data.defaults.window.start,
      end: catalogue.data.defaults.window.end,
      asOf: toLocalInput(catalogue.data.defaults.as_of),
      bucket: 'DAY',
      sellerId: catalogue.data.defaults.our_seller_id,
    });
    setSelected(catalogue.data.functions[0]?.name ?? null);
  }, [catalogue.data, selected]);

  const buildOptions = useCallback(
    () => ({
      window: { start: options.start, end: options.end },
      as_of: options.asOf,
      bucket: options.bucket,
      our_seller_id: options.sellerId,
    }),
    [options],
  );

  const execute = useCallback(
    async (inputsJson, configJson) => {
      if (!currentSpec || !options) return;
      setRunning(true);
      setError(null);
      try {
        if (source === 'live') {
          const response = await runFunctionLive(token, {
            function: currentSpec.name,
            config: configJson,
            options: buildOptions(),
          });
          setResult(response.result);
          setLiveNotes(response.live_notes || []);
          setInputCounts(
            Object.entries(response.input_counts || {})
              .map(([key, count]) => `${key}: ${count}`)
              .join(', '),
          );
        } else {
          const response = await runFunction(token, {
            function: currentSpec.name,
            inputs: inputsJson,
            config: configJson,
            options: buildOptions(),
          });
          setResult(response.result);
          setLiveNotes(null);
        }
      } catch (err) {
        setResult(null);
        setError(err.message);
      } finally {
        setRunning(false);
      }
    },
    [currentSpec, options, token, buildOptions, source],
  );

  // Loading a function pulls its sample payload and runs it straight away, so
  // the page is never sitting on an empty result waiting to be told to work.
  // In live mode the sample still populates the config editor, but the run
  // fetches its inputs from the connected account server-side.
  useEffect(() => {
    if (!currentSpec || !token || !options) return;
    let cancelled = false;

    (async () => {
      setError(null);
      setResult(null);
      setLiveNotes(null);
      if (source === 'live' && currentSpec.live_supported === false) {
        setError(currentSpec.live_reason || 'This function has no live data source yet.');
        return;
      }
      try {
        const sample = await getSample(token, currentSpec.name);
        if (cancelled) return;
        setInputsText(JSON.stringify(sample.inputs, null, 1));
        setConfigText(JSON.stringify(sample.config, null, 1));
        setInputCounts(
          Object.entries(sample.inputs)
            .map(([key, rows]) => `${key}: ${rows.length}`)
            .join(', '),
        );
        await execute(sample.inputs, sample.config);
      } catch (err) {
        if (!cancelled) setError(err.message);
      }
    })();

    return () => {
      cancelled = true;
    };
    // execute changes with every option edit; re-fetching the sample then would
    // discard the user's edits, so only the function and data source drive this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSpec?.name, token, source]);

  // Switching source swaps the window too: the fixture month for the sample
  // store, the last 30 days for the connected account.
  const switchSource = (next) => {
    if (next === source || !catalogue.data) return;
    setSource(next);
    if (next === 'live') {
      const today = new Date();
      const start = new Date(today);
      start.setDate(start.getDate() - 30);
      const iso = (d) => d.toISOString().slice(0, 10);
      setOptions((prev) => ({
        ...prev,
        start: iso(start),
        end: iso(today),
        asOf: today.toISOString().slice(0, 16),
      }));
    } else {
      setOptions((prev) => ({
        ...prev,
        start: catalogue.data.defaults.window.start,
        end: catalogue.data.defaults.window.end,
        asOf: toLocalInput(catalogue.data.defaults.as_of),
      }));
    }
  };

  const handleRun = () => {
    let inputsJson;
    let configJson;
    try {
      inputsJson = source === 'live' ? null
        : inputsText.trim() ? JSON.parse(inputsText) : {};
      configJson = configText.trim() ? JSON.parse(configText) : {};
    } catch (err) {
      setResult(null);
      setError(`Invalid JSON in the editors — ${err.message}`);
      return;
    }
    execute(inputsJson, configJson);
  };

  const handleReset = async () => {
    if (!currentSpec) return;
    try {
      const sample = await getSample(token, currentSpec.name);
      setInputsText(JSON.stringify(sample.inputs, null, 1));
      setConfigText(JSON.stringify(sample.config, null, 1));
      await execute(sample.inputs, sample.config);
    } catch (err) {
      setError(err.message);
    }
  };

  const header = (
    <div className={styles.header}>
      <button className={styles.backButton} onClick={() => navigate('/dashboard/seller-intelligence')}>
        <FiArrowLeft /> Back
      </button>
      <div className={styles.titleSection}>
        <div className={styles.iconTitle}>
          <h1>Metrics Playground</h1>
        </div>
        <p>Run the 28 Seller Intelligence metric functions against a generated sample store or your connected Amazon account</p>
      </div>
    </div>
  );

  if (catalogue.isError) {
    return (
      <div className={styles.featureContainer}>
        {header}
        <div className={styles.content}>
          <section className={local.card}>
            <h3 className={local.cardTitle}>Playground unavailable</h3>
            {catalogue.error?.disabled ? (
              <>
                <p>
                  The metric harness is a development-only surface and is not registered
                  on this backend.
                </p>
                <p className={local.note}>
                  Start the API with <code className={local.inlineCode}>FLASK_DEBUG=True</code> or{' '}
                  <code className={local.inlineCode}>ENABLE_METRICS_PLAYGROUND=true</code> to switch it on.
                </p>
              </>
            ) : (
              <p>{catalogue.error?.message}</p>
            )}
          </section>
        </div>
      </div>
    );
  }

  if (catalogue.isLoading || !options) {
    return (
      <div className={styles.featureContainer}>
        {header}
        <div className={styles.content}>
          <p className={local.muted}>Loading the function catalogue…</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.featureContainer}>
      {header}
      <div className={styles.content}>
        <div className={local.layout}>
          <nav className={local.rail}>
            {families.map(([family, items]) => (
              <div key={family}>
                <div className={local.familyLabel}>{family}</div>
                {items.map((spec) => (
                  <button
                    key={spec.name}
                    type="button"
                    className={`${local.railItem} ${spec.name === selected ? local.railItemOn : ''}`}
                    onClick={() => setSelected(spec.name)}
                    disabled={source === 'live' && spec.live_supported === false}
                    title={source === 'live' && spec.live_supported === false
                      ? spec.live_reason : undefined}
                  >
                    {spec.name}
                  </button>
                ))}
              </div>
            ))}
          </nav>

          <div className={local.main}>
            {currentSpec && (
              <div className={local.intro}>
                <h2 className={local.functionName}>{currentSpec.name}</h2>
                <p className={local.summary}>{currentSpec.summary}</p>
                <p className={local.muted}>
                  {currentSpec.doc_name !== '—'
                    ? <>Doc name: <code className={local.inlineCode}>{currentSpec.doc_name}</code></>
                    : 'Not named in the requirements doc — added from the feasibility sheet.'}
                </p>
              </div>
            )}

            <div className={local.controls}>
              <div className={local.field}>
                <span>Data source</span>
                <div className={local.sourceToggle}>
                  <button
                    type="button"
                    className={`${local.sourceBtn} ${source === 'sample' ? local.sourceBtnOn : ''}`}
                    onClick={() => switchSource('sample')}
                  >
                    Sample store
                  </button>
                  <button
                    type="button"
                    className={`${local.sourceBtn} ${source === 'live' ? local.sourceBtnOn : ''}`}
                    onClick={() => switchSource('live')}
                  >
                    Connected account
                  </button>
                </div>
              </div>
              <label className={local.field}>
                <span>Window start</span>
                <input
                  type="date"
                  value={options.start}
                  onChange={(e) => setOptions({ ...options, start: e.target.value })}
                />
              </label>
              <label className={local.field}>
                <span>Window end</span>
                <input
                  type="date"
                  value={options.end}
                  onChange={(e) => setOptions({ ...options, end: e.target.value })}
                />
              </label>
              <label className={local.field}>
                <span>As of</span>
                <input
                  type="datetime-local"
                  value={options.asOf}
                  onChange={(e) => setOptions({ ...options, asOf: e.target.value })}
                />
              </label>
              <label className={local.field}>
                <span>Bucket</span>
                <select
                  value={options.bucket}
                  onChange={(e) => setOptions({ ...options, bucket: e.target.value })}
                >
                  {BUCKETS.map((bucket) => <option key={bucket}>{bucket}</option>)}
                </select>
              </label>
              <label className={local.field}>
                <span>Our seller id</span>
                <input
                  value={options.sellerId}
                  onChange={(e) => setOptions({ ...options, sellerId: e.target.value })}
                />
              </label>

              <button type="button" className={local.primary} onClick={handleRun} disabled={running}>
                <FiPlay /> {running ? 'Running…' : 'Run'}
              </button>
              <button type="button" className={local.ghost} onClick={handleReset}>
                <FiRefreshCw /> Reset inputs
              </button>
              <button type="button" className={local.ghost} onClick={() => setShowRaw((v) => !v)}>
                <FiCode /> {showRaw ? 'Rendered' : 'Raw JSON'}
              </button>
            </div>

            {error && (
              <section className={`${local.card} ${local.errorCard}`}>
                <strong>{error}</strong>
              </section>
            )}

            {liveNotes && liveNotes.length > 0 && (
              <section className={local.card}>
                <h3 className={local.cardTitle}>Live data notes</h3>
                {liveNotes.map((note) => (
                  <p key={note} className={local.note}>{note}</p>
                ))}
              </section>
            )}

            <ResultView result={result} raw={showRaw} />

            <div className={local.editors}>
              <section className={local.card}>
                <h3 className={local.cardTitle}>
                  Inputs <span className={local.muted}>{inputCounts && `— ${inputCounts}`}</span>
                </h3>
                {source === 'live' ? (
                  <p className={local.note}>
                    Fetched from the connected Amazon account on every run.
                    The inputs editor applies to the sample store only.
                  </p>
                ) : (
                  <textarea
                    className={local.editor}
                    spellCheck={false}
                    value={inputsText}
                    onChange={(e) => setInputsText(e.target.value)}
                  />
                )}
              </section>
              <section className={local.card}>
                <h3 className={local.cardTitle}>Config</h3>
                <textarea
                  className={local.editor}
                  spellCheck={false}
                  value={configText}
                  onChange={(e) => setConfigText(e.target.value)}
                />
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MetricsPlayground;
